import Button from '@blaze-react/button';
import Input from '@blaze-react/input';
import Select from '@blaze-react/select';
import { useToasts } from '@blaze-react/toaster';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { PRIVACY } from '../../constants';
import {
  checkValidEmail,
  RESIDENCE_COUNTRIES,
  pushToDataLayer,
  submitSalesforceNewsletter,
  userRoleOptions
} from '../../utils';

const roleOptions = [['', 'Which category best describes you?'], ...userRoleOptions];

const countryOfResidenceOptions = [
  ['', 'What is your country of residence?'],
  ...RESIDENCE_COUNTRIES.map(country => [country, country])
];

const SignupForm = ({
  productBriefing,
  productBusinessNews,
  productDeepDive,
  productEvents,
  productMarketing,
  productPodcast,
  closeModal,
  embedded,
  toastCaption,
  salesForceCampaignId,
  title,
  children
}) => {
  const [submissionSuccess, setSubmissionSuccess] = useState(null);
  const [submitState, setSubmitState] = useState(null);
  const { addToast } = useToasts();
  const [values, setValues] = useState({
    additionalTermsAccepted: null,
    countryOfResidence: '',
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    termsAccepted: false,
    checkProductBriefing: false,
    checkProductBusinessNews: false,
    checkProductDeepDive: false,
    checkProductEvents: false,
    checkProductMarketing: false,
    checkProductPodcast: false
  });

  const atLeastOneProductChecked = ({
    checkProductBriefing,
    checkProductBusinessNews,
    checkProductDeepDive,
    checkProductEvents,
    checkProductMarketing,
    checkProductPodcast
  }) =>
    checkProductBriefing ||
    checkProductBusinessNews ||
    checkProductDeepDive ||
    checkProductEvents ||
    checkProductMarketing ||
    checkProductPodcast;

  const {
    additionalTermsAccepted,
    countryOfResidence,
    firstName,
    lastName,
    email,
    role,
    termsAccepted,
    checkProductBriefing,
    checkProductBusinessNews,
    checkProductDeepDive,
    checkProductEvents,
    checkProductMarketing,
    checkProductPodcast
  } = values;

  const roleValid = role && role !== 'Please choose...';
  const termsValid = RESIDENCE_COUNTRIES.includes(countryOfResidence) && !!termsAccepted;

  const isFormFull = !!(
    additionalTermsAccepted !== null &&
    firstName &&
    lastName &&
    roleValid &&
    checkValidEmail(email) &&
    atLeastOneProductChecked(values) &&
    termsValid
  );

  const wrapperClass = classnames('newsletter-signup__form', {
    'newsletter-signup__form--embedded': embedded,
    'newsletter-signup__form--in-modal': !embedded
  });

  const buttonClass = classnames('newsletter-signup__submit', {
    'newsletter-signup__submit--disabled': !isFormFull
  });

  const handleChange = ({ target: { name: targetName } }, value) => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      termsAccepted,
      checkProductBriefing,
      checkProductBusinessNews,
      checkProductDeepDive,
      checkProductEvents,
      checkProductMarketing,
      checkProductPodcast,
      [targetName]: value
    });
  };

  const handleCountryOfResidenceChange = value => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence: value,
      firstName,
      lastName,
      email,
      role,
      termsAccepted,
      checkProductBriefing,
      checkProductBusinessNews,
      checkProductDeepDive,
      checkProductEvents,
      checkProductMarketing,
      checkProductPodcast
    });
  };

  const handleCheckboxChange = targetName => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      termsAccepted,
      checkProductBriefing:
        targetName === 'checkProductBriefing' ? !checkProductBriefing : checkProductBriefing,
      checkProductBusinessNews:
        targetName === 'checkProductBusinessNews'
          ? !checkProductBusinessNews
          : checkProductBusinessNews,
      checkProductDeepDive:
        targetName === 'checkProductDeepDive' ? !checkProductDeepDive : checkProductDeepDive,
      checkProductEvents:
        targetName === 'checkProductEvents' ? !checkProductEvents : checkProductEvents,
      checkProductMarketing:
        targetName === 'checkProductMarketing' ? !checkProductMarketing : checkProductMarketing,
      checkProductPodcast:
        targetName === 'checkProductPodcast' ? !checkProductPodcast : checkProductPodcast
    });
  };

  const handleTermsCheckboxChange = () => {
    setValues({
      additionalTermsAccepted,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      termsAccepted: !termsAccepted,
      checkProductBriefing,
      checkProductBusinessNews,
      checkProductDeepDive,
      checkProductEvents,
      checkProductMarketing,
      checkProductPodcast
    });
  };

  const handleAdditionalTermsChange = val => {
    setValues({
      additionalTermsAccepted: val,
      countryOfResidence,
      firstName,
      lastName,
      email,
      role,
      termsAccepted,
      checkProductBriefing,
      checkProductBusinessNews,
      checkProductDeepDive,
      checkProductEvents,
      checkProductMarketing,
      checkProductPodcast
    });
  };

  useEffect(() => {
    const doSubmit = async () => {
      if (submitState === null) return;

      setSubmitState(null);

      const products = [];
      if (submitState.values.checkProductBriefing) {
        products.push('briefing');
      }

      if (submitState.values.checkProductBusinessNews) {
        products.push('businessNews');
      }

      if (submitState.values.checkProductDeepDive) {
        products.push('deepDive');
      }

      if (submitState.values.checkProductEvents) {
        products.push('events');
      }

      if (submitState.values.checkProductMarketing) {
        products.push('marketing');
      }

      if (submitState.values.checkProductPodcast) {
        products.push('podcast');
      }

      let success = false;
      try {
        await submitSalesforceNewsletter(
          submitState.values.firstName,
          submitState.values.lastName,
          submitState.values.email,
          products,
          {
            country: submitState.values.countryOfResidence,
            additionalTermsAccepted: submitState.values.additionalTermsAccepted
          },
          submitState.values.role,
          salesForceCampaignId
        );

        const basePayload = submitState.values.role
          ? { user: { role: submitState.values.role } }
          : {};

        products.forEach(product =>
          pushToDataLayer({ ...basePayload, event: 'Newsletter Signup', newsletter_name: product })
        );

        addToast(toastCaption, {
          appearance: 'success',
          autoDismiss: true
        });
        success = true;
      } catch ({ message }) {
        addToast(message, {
          appearance: 'error',
          autoDismiss: true
        });
      }

      setValues({
        additionalTermsAccepted: null,
        countryOfResidence: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        termsAccepted: false,
        checkProductBriefing: false,
        checkProductBusinessNews: false,
        checkProductDeepDive: false,
        checkProductEvents: false,
        checkProductMarketing: false,
        checkProductPodcast: false
      });

      if (success) {
        setSubmissionSuccess(true);

        if (!children || (Array.isArray(children) && !children.length)) closeModal();
      }
    };
    doSubmit();
  }, [
    addToast,
    submitState,
    pushToDataLayer,
    setValues,
    closeModal,
    submitSalesforceNewsletter,
    toastCaption,
    salesForceCampaignId
  ]);

  if (submissionSuccess && children) return children;

  return (
    <div className={wrapperClass}>
      {!embedded && (
        <div role="button" className="newsletter-signup__close" onClick={() => closeModal()}>
          <i className="fas fa-times" />
        </div>
      )}

      {submissionSuccess && children}

      {!submissionSuccess && (
        <>
          <div className="newsletter-signup__header">
            <div className="newsletter-signup__title-wrapper">
              <h2 className="newsletter-signup__title">{title}</h2>
            </div>
          </div>

          <form className="newsletter-signup__body">
            <div className="newsletter-signup__first-and-last-name">
              <Input
                type="text"
                name="firstName"
                placeholder="* First name"
                className="newsletter-signup__input"
                required="required"
                onChange={({ event, value }) => handleChange(event, value)}
                value={firstName}
              />

              <Input
                type="text"
                name="lastName"
                placeholder="* Last name"
                className="newsletter-signup__input"
                required="required"
                onChange={({ event, value }) => handleChange(event, value)}
                value={lastName}
              />
            </div>

            <Input
              type="text"
              name="email"
              placeholder="* Email"
              className="newsletter-signup__input"
              required="required"
              onChange={({ event, value }) => handleChange(event, value)}
              value={email}
            />

            <Select
              name="countryOfResidence"
              className="newsletter-signup__input"
              options={countryOfResidenceOptions}
              onChange={({ value }) => handleCountryOfResidenceChange(value)}
              selected={countryOfResidence}
            />

            <Select
              name="role"
              className="newsletter-signup__input"
              options={roleOptions}
              onChange={({ event, value }) => handleChange(event, value)}
              selected={role}
            />

            <div className="newsletter-signup__checkboxes-wrapper">
              {productBriefing && (
                <div className="form-field form-field--checkbox" role="button">
                  <input
                    readOnly
                    type="checkbox"
                    className="form-checkbox"
                    checked={checkProductBriefing}
                    name="checkProductBriefing"
                    onClick={() => handleCheckboxChange('checkProductBriefing')}
                  />

                  <label
                    className="form-field__label"
                    onClick={() => handleCheckboxChange('checkProductBriefing')}>
                    <b>BOAT Briefing:</b> Superyacht industry news and features every weekday
                  </label>
                </div>
              )}

              {productDeepDive && (
                <div className="form-field form-field--checkbox" role="button">
                  <input
                    readOnly
                    type="checkbox"
                    className="form-checkbox"
                    checked={checkProductDeepDive}
                    name="checkProductDeepDive"
                    onClick={() => handleCheckboxChange('checkProductDeepDive')}
                  />

                  <label
                    className="form-field__label"
                    onClick={() => handleCheckboxChange('checkProductDeepDive')}>
                    <b>Deep Dive:</b> Get on board the world's finest yachts every Saturday
                  </label>
                </div>
              )}

              {productBusinessNews && (
                <div className="form-field form-field--checkbox" role="button">
                  <input
                    readOnly
                    type="checkbox"
                    className="form-checkbox"
                    checked={checkProductBusinessNews}
                    name="checkProductBusinessNews"
                    onClick={() => handleCheckboxChange('checkProductBusinessNews')}
                  />

                  <label
                    className="form-field__label"
                    onClick={() => handleCheckboxChange('checkProductBusinessNews')}>
                    <b>BOATPro News:</b> B2B news, insight and reports every Wednesday
                  </label>
                </div>
              )}

              {productEvents && (
                <div className="form-field form-field--checkbox" role="button">
                  <input
                    readOnly
                    type="checkbox"
                    className="form-checkbox"
                    checked={checkProductEvents}
                    name="checkProductEvents"
                    onClick={() => handleCheckboxChange('checkProductEvents')}
                  />

                  <label
                    className="form-field__label"
                    onClick={() => handleCheckboxChange('checkProductEvents')}>
                    <b>Events:</b> Keep up-to-date with BOAT's conferences and awards
                  </label>
                </div>
              )}

              {productMarketing && (
                <div className="form-field form-field--checkbox" role="button">
                  <input
                    readOnly
                    type="checkbox"
                    className="form-checkbox"
                    checked={checkProductMarketing}
                    name="checkProductMarketing"
                    onClick={() => handleCheckboxChange('checkProductMarketing')}
                  />

                  <label
                    className="form-field__label"
                    onClick={() => handleCheckboxChange('checkProductMarketing')}>
                    <b>BOAT Marketing:</b> Magazine subscriptions and exclusive offers
                  </label>
                </div>
              )}

              {productPodcast && (
                <div className="form-field form-field--checkbox" role="button">
                  <input
                    readOnly
                    type="checkbox"
                    className="form-checkbox"
                    checked={checkProductPodcast}
                    name="checkProductPodcast"
                    onClick={() => handleCheckboxChange('checkProductPodcast')}
                  />

                  <label
                    className="form-field__label"
                    onClick={() => handleCheckboxChange('checkProductPodcast')}>
                    <b>Editor's choice:</b> The week's important stories, plus the Boat Briefing
                    podcast
                  </label>
                </div>
              )}
            </div>

            <div className="newsletter-signup__checkbox">
              <input
                readOnly
                className="newsletter-signup__checkbox__box"
                type="checkbox"
                checked={termsAccepted}
                name="termsAccepted"
                onClick={() => handleTermsCheckboxChange()}
              />

              <label
                className="newsletter-signup__checkbox__label"
                onClick={() => handleTermsCheckboxChange()}>
                I agree to the{' '}
                <a
                  href={PRIVACY.TERMS_HREF}
                  className="boat-pro-form__checkbox__link"
                  target="_blank"
                  rel="noreferrer">
                  terms and conditions
                </a>{' '}
                and{' '}
                <a
                  href={PRIVACY.HREF}
                  className="boat-pro-form__checkbox__link"
                  target="_blank"
                  rel="noreferrer">
                  privacy notice
                </a>
                .
              </label>
            </div>

            <div className="newsletter-signup__radio">
              <label className="newsletter-signup__radio__label">
                I would like to receive tailored information about events and products from BOAT
                International Media Ltd, an Informa Group Company. You can opt out at any time by
                clicking unsubscribe or contacting us. For Further details, see our{' '}
                <a
                  href={PRIVACY}
                  className="newsletter-signup__checkbox__footer-link"
                  target="_blank"
                  rel="noreferrer">
                  privacy notice
                </a>
                .
              </label>

              <div className="newsletter-signup__radio__boxes">
                <input
                  readOnly
                  type="radio"
                  name="additionalTermsAccepted"
                  value="yes"
                  className="newsletter-signup__radio__box"
                  checked={additionalTermsAccepted === true}
                  onClick={() => handleAdditionalTermsChange(true)}
                />
                <label
                  className="newsletter-signup__radio__box-label"
                  onClick={() => handleAdditionalTermsChange(true)}>
                  Yes
                </label>{' '}
                <input
                  readOnly
                  type="radio"
                  name="additionalTermsAccepted"
                  value="no"
                  className="newsletter-signup__radio__box"
                  checked={additionalTermsAccepted === false}
                  onClick={() => handleAdditionalTermsChange(false)}
                />
                <label
                  className="newsletter-signup__radio__box-label"
                  onClick={() => handleAdditionalTermsChange(false)}>
                  No
                </label>
              </div>
            </div>

            <Button
              type="button"
              name="button"
              data-gtm-id="newsletter-signup"
              className={buttonClass}
              disabled={!isFormFull}
              onClick={() => setSubmitState({ values, setValues })}>
              Submit
            </Button>
          </form>
        </>
      )}
    </div>
  );
};

SignupForm.propTypes = {
  productBriefing: PropTypes.bool.isRequired,
  productBusinessNews: PropTypes.bool.isRequired,
  productDeepDive: PropTypes.bool.isRequired,
  productEvents: PropTypes.bool.isRequired,
  productMarketing: PropTypes.bool.isRequired,
  productPodcast: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  toastCaption: PropTypes.string.isRequired,
  salesForceCampaignId: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  embedded: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired
};

SignupForm.defaultProps = {
  salesForceCampaignId: ''
};

export default SignupForm;
